import React, { type ReactNode } from "react";
import type { BaseProps } from "@purpur/common-types";
import c from "classnames";

import styles from "./heading.module.scss";

export const HeadingTag = {
  H1: "h1",
  H2: "h2",
  H3: "h3",
  H4: "h4",
  H5: "h5",
  H6: "h6",
} as const;

export type HeadingTagType = (typeof HeadingTag)[keyof typeof HeadingTag];

export const TitleVariant = {
  TITLE100: "title-100",
  TITLE200: "title-200",
  TITLE300: "title-300",
  TITLE400: "title-400",
  TITLE500: "title-500",
  TITLE600: "title-600",
  TITLE700: "title-700",
  SUBSECTION100: "subsection-100",
} as const;

export type TitleVariantType = (typeof TitleVariant)[keyof typeof TitleVariant];

export const DisplayVariant = {
  DISPLAY5: "display-5",
  DISPLAY10: "display-10",
  DISPLAY15: "display-15",
  DISPLAY20: "display-20",
  DISPLAY25: "display-25",
  DISPLAY50: "display-50",
  DISPLAY100: "display-100",
  DISPLAY200: "display-200",
} as const;

export type DisplayVariantType = (typeof DisplayVariant)[keyof typeof DisplayVariant];

export type HeadingVariant = TitleVariantType | DisplayVariantType;

export type HeadingProps<T extends HeadingTagType> = Omit<BaseProps<T>, "children"> & {
  children: ReactNode;
  enableHyphenation?: boolean;
  negative?: boolean;
  tag: HeadingTagType;
  variant?: HeadingVariant;
};

const rootClassName = "purpur-heading";

export const Heading = <T extends HeadingTagType>({
  children,
  className = "",
  enableHyphenation = false,
  negative = false,
  tag,
  variant = TitleVariant.TITLE100,
  ...props
}: HeadingProps<T>) => {
  const Tag = tag;
  const classes = c([
    className,
    styles[rootClassName],
    styles[`${rootClassName}--${variant}`],
    {
      [styles[`${rootClassName}--hyphens`]]: enableHyphenation,
      [styles[`${rootClassName}--negative`]]: negative,
    },
  ]);

  return (
    <Tag className={classes} {...props}>
      {children}
    </Tag>
  );
};

Heading.displayName = "Heading";
